/**
 * Account drawer (rail avatar): active profile + level, switch profile, edit / add profile,
 * settings, account, language, restore purchase, help, feedback, unlock CTA. Hidden cheats
 * triggers on the left / right edges: long-press left, right, left (≥ 600 ms each) opens /cheats.
 */
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MAX_PROFILES, levelFor, needsFamilyUpgrade } from '@sketchling/core';
import { EMPTY_PROGRESS, selectTier, useAccess, useActiveProfile, useApp, useStoreBilling } from '../state/store';
import { api } from '../lib/api';
import { restorePurchases } from '../lib/purchases';
import { useT } from '../lib/i18n';
import { play } from '../lib/sfx';
import { Avatar } from './characters';
import { Icon, type IconName } from './icons';
import { Button, IconButton, Modal, toast } from './ui';
import { openExternal, useParentalGate } from '../screens/parents/ParentalGate';

const LONG_PRESS_MS = 600;
const SEQUENCE: ('left' | 'right')[] = ['left', 'right', 'left'];

function useCheatsSequence(onUnlock: () => void) {
  const seq = useRef<('left' | 'right')[]>([]);
  const down = useRef<{ side: 'left' | 'right'; at: number } | null>(null);
  const last = useRef(0);
  const start = (side: 'left' | 'right') => {
    down.current = { side, at: performance.now() };
  };
  const end = (side: 'left' | 'right') => {
    const d = down.current;
    down.current = null;
    if (!d || d.side !== side) return;
    const now = performance.now();
    if (now - d.at < LONG_PRESS_MS) {
      seq.current = [];
      return;
    }
    if (now - last.current > 10_000) seq.current = [];
    last.current = now;
    seq.current = [...seq.current, side];
    useApp.getState().logEvent(side === 'left' ? 'Cheats Left Long Press' : 'Cheats Right Long Press');
    const n = seq.current.length;
    if (seq.current[n - 1] !== SEQUENCE[n - 1]) seq.current = side === SEQUENCE[0] ? [side] : [];
    if (seq.current.length === SEQUENCE.length) {
      seq.current = [];
      onUnlock();
    }
  };
  const props = (side: 'left' | 'right') => ({
    onPointerDown: () => start(side),
    onPointerUp: () => end(side),
    onPointerLeave: () => (down.current = null),
    onContextMenu: (e: React.MouseEvent) => e.preventDefault(),
  });
  return props;
}

export function AccountDrawer({ onClose }: { onClose: () => void }) {
  const t = useT();
  const gate = useParentalGate(); // external links only behind the parental gate (P0-3)
  const nav = useNavigate();
  const profile = useActiveProfile();
  const profiles = useApp((s) => s.profiles);
  const progress = useApp((s) => (s.activeProfileId ? (s.progress[s.activeProfileId] ?? EMPTY_PROGRESS) : EMPTY_PROGRESS));
  const lang = useApp((s) => s.settings.language);
  const tier = useApp(selectTier);
  const updateSettings = useApp((s) => s.updateSettings);
  const switchProfile = useApp((s) => s.switchProfile);
  const setCheats = useApp((s) => s.setCheats);
  const access = useAccess();
  const billing = useStoreBilling();
  const parentArea = useApp((s) => s.config.parentArea);
  const [feedback, setFeedback] = useState(false);
  const go = (to: string) => {
    play('click');
    onClose();
    nav(to);
  };
  const triggers = useCheatsSequence(() => {
    play('magic');
    setCheats({ unlocked: true });
    go('/cheats');
  });
  const level = levelFor(progress.totalSparks).level;

  const item = (icon: IconName, label: string, onClick: () => void, testId: string) => (
    <button type="button" className="drawer-item" onClick={onClick} data-testid={testId}>
      <Icon name={icon} size={22} />
      <span>{label}</span>
    </button>
  );

  return (
    <div className="scrim drawer-scrim" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <aside className="drawer account-drawer" aria-label={t('acc.drawer.title')} data-testid="account-drawer">
        {/* Hidden cheats gesture: dev builds only (no hidden features in store builds). */}
        {import.meta.env.DEV && <div className="cheats-trigger left" data-testid="cheats-left" aria-hidden {...triggers('left')} />}
        {import.meta.env.DEV && <div className="cheats-trigger right" data-testid="cheats-right" aria-hidden {...triggers('right')} />}
        <IconButton icon="close" label={t('close')} className="drawer-close" onClick={onClose} />
        <div className="drawer-head">
        {profile && (
          <div className="drawer-profile row">
            <Avatar avatar={profile.avatar} character={profile.character} size={64} />
            <div className="col tight grow">
              <b className="drawer-name">{profile.name}</b>
              <span className="chip pill-gold">{t('level', { n: level })}</span>
            </div>
          </div>
        )}
        {profiles.length > 1 && (
          <div className="drawer-section">
            <p className="drawer-label">{t('acc.drawer.switch')}</p>
            <div className="drawer-switch">
              {profiles.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className={`switch-avatar${p.id === profile?.id ? ' on' : ''}`}
                  aria-label={p.name}
                  aria-pressed={p.id === profile?.id}
                  data-testid={`drawer-profile-${p.name}`}
                  onClick={() => {
                    play('sparkle');
                    switchProfile(p.id);
                    onClose();
                  }}
                >
                  <Avatar avatar={p.avatar} character={p.character} size={44} />
                  <small>{p.name}</small>
                </button>
              ))}
            </div>
          </div>
        )}
        </div>
        <nav className="drawer-list">
          {item('edit', t('acc.drawer.editProfiles'), () => go('/profiles'), 'drawer-edit-profiles')}
          {profiles.length < MAX_PROFILES &&
            item('plus', t('prof.add'), () => go(needsFamilyUpgrade(tier, profiles.length) ? '/paywall?from=familyUpgrade' : '/profiles?add=1'), 'drawer-add-profile')}
          {item('settings', t('acc.drawer.settings'), () => go('/settings'), 'drawer-settings')}
          {parentArea && item('heart', t('par.entry'), () => go('/parents'), 'drawer-parents')}
          {item('users', t('acc.drawer.account'), () => gate.guard('account', () => go('/account')), 'drawer-account')}
          <div className="drawer-item lang-row">
            <Icon name="globe" size={22} />
            <span className="grow">{t('acc.drawer.language')}</span>
            <div className="mini-seg" role="group" aria-label={t('acc.drawer.language')}>
              {(['en', 'vi'] as const).map((l) => (
                <button key={l} type="button" className={lang === l ? 'on' : ''} aria-pressed={lang === l} data-testid={`drawer-lang-${l}`} onClick={() => (play('slider'), updateSettings({ language: l }))}>
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
          {billing && item(
            'replay',
            t('acc.restore'),
            async () => {
              play('click');
              const ok = await restorePurchases();
              toast(ok ? t('acc.restored') : t('acc.nothingToRestore'));
            },
            'drawer-restore',
          )}
          {item('help', t('acc.drawer.help'), () => gate.guard('help', () => openExternal('mailto:hello@sketchling.app?subject=Sketchling')), 'drawer-help')}
          {item('mail', t('acc.drawer.feedback'), () => (play('click'), setFeedback(true)), 'drawer-feedback')}
          {import.meta.env.DEV && item('bolt', t('acc.drawer.debug'), () => go('/cheats'), 'drawer-debug')}
        </nav>
        {billing && !access.premium && (
          <Button block icon="crown" onClick={() => go('/paywall?from=drawer')} data-testid="drawer-unlock">
            {t('acc.drawer.unlock')}
          </Button>
        )}
      </aside>
      {feedback && <FeedbackModal onClose={() => setFeedback(false)} />}
      {gate.element}
    </div>
  );
}

export function FeedbackModal({ onClose }: { onClose: () => void }) {
  const t = useT();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const profile = useActiveProfile();
  return (
    <Modal onClose={onClose} label={t('acc.feedback.title')} testId="feedback-modal">
      <div className="col">
        <h2>{t('acc.feedback.title')}</h2>
        <textarea className="field" rows={4} maxLength={2000} value={text} placeholder={t('acc.feedback.placeholder')} data-testid="feedback-text" onChange={(e) => setText(e.target.value)} />
        <Button
          disabled={!text.trim() || busy}
          data-testid="feedback-send"
          onClick={async () => {
            setBusy(true);
            try {
              await api.feedback('general', { text: text.trim() }, profile?.remoteId);
              useApp.getState().logEvent('Feedback Sent');
              toast(t('acc.feedback.thanks'));
              onClose();
            } catch {
              toast(t('acc.feedback.failed'));
            } finally {
              setBusy(false);
            }
          }}
        >
          {t('acc.feedback.send')}
        </Button>
      </div>
    </Modal>
  );
}
